import React from 'react';
import {
  ConditionGroup,
  RoutingCondition,
  ConditionType,
  createEmptyTimeCondition,
  createEmptyGeographicCondition,
  createEmptyDeviceCondition,
} from '../../../types/qr.types';
import { TimeConditionEditor } from './conditions/TimeConditionEditor';
import { GeographicConditionEditor } from './conditions/GeographicConditionEditor';
import { DeviceConditionEditor } from './conditions/DeviceConditionEditor';

interface RuleConditionEditorProps {
  conditions: ConditionGroup;
  onChange: (conditions: ConditionGroup) => void;
  hasGeoRoutingFeature?: boolean; // Whether user has geographic routing feature
}

export function RuleConditionEditor({ conditions, onChange, hasGeoRoutingFeature = false }: RuleConditionEditorProps) {
  const handleOperatorChange = (operator: 'AND' | 'OR') => {
    onChange({ ...conditions, operator });
  };

  const handleConditionChange = (index: number, condition: RoutingCondition) => {
    const newItems = [...conditions.items];
    newItems[index] = condition;
    onChange({ ...conditions, items: newItems });
  };

  const handleRemoveCondition = (index: number) => {
    const newItems = conditions.items.filter((_, i) => i !== index);
    onChange({ ...conditions, items: newItems });
  };

  const handleAddCondition = (type: ConditionType) => {
    let newCondition: RoutingCondition;
    switch (type) {
      case 'time':
        newCondition = createEmptyTimeCondition();
        break;
      case 'geographic':
        newCondition = createEmptyGeographicCondition();
        break;
      case 'device':
        newCondition = createEmptyDeviceCondition();
        break;
    }
    onChange({ ...conditions, items: [...conditions.items, newCondition] });
  };

  const renderCondition = (condition: RoutingCondition, index: number) => {
    switch (condition.type) {
      case 'time':
        return (
          <TimeConditionEditor
            key={index}
            condition={condition}
            onChange={(c) => handleConditionChange(index, c)}
            onRemove={() => handleRemoveCondition(index)}
          />
        );
      case 'geographic':
        return (
          <GeographicConditionEditor
            key={index}
            condition={condition}
            onChange={(c) => handleConditionChange(index, c)}
            onRemove={() => handleRemoveCondition(index)}
            isLocked={!hasGeoRoutingFeature}
          />
        );
      case 'device':
        return (
          <DeviceConditionEditor
            key={index}
            condition={condition}
            onChange={(c) => handleConditionChange(index, c)}
            onRemove={() => handleRemoveCondition(index)}
          />
        );
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-gray-900">Conditions</h4>
        {conditions.items.length > 1 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Match:</span>
            <select
              value={conditions.operator}
              onChange={(e) => handleOperatorChange(e.target.value as 'AND' | 'OR')}
              className="h-8 px-2 rounded border border-[--clinqr-border]/20 text-sm focus:outline-none focus:border-[--clinqr-primary] bg-white"
            >
              <option value="AND">ALL conditions (AND)</option>
              <option value="OR">ANY condition (OR)</option>
            </select>
          </div>
        )}
      </div>

      {conditions.items.length === 0 ? (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
          <p className="text-gray-500 mb-4">No conditions added. This rule will always match.</p>
          <p className="text-sm text-gray-400">Add a condition to make this rule trigger based on time, location, or device.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {conditions.items.map((condition, index) => (
            <div key={index}>
              {index > 0 && (
                <div className="flex items-center justify-center py-2">
                  <span className="px-3 py-1 bg-gray-100 rounded-full text-sm font-medium text-gray-600">
                    {conditions.operator}
                  </span>
                </div>
              )}
              {renderCondition(condition, index)}
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-2">
        <button
          type="button"
          onClick={() => handleAddCondition('time')}
          className="flex items-center gap-2 px-3 py-2 bg-[--clinqr-bg-subtle] text-[--clinqr-text-primary] border border-[--clinqr-border]/10 rounded-lg hover:bg-gray-200 transition-colors text-sm"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Time Condition
        </button>
        <button
          type="button"
          onClick={() => !hasGeoRoutingFeature ? null : handleAddCondition('geographic')}
          disabled={!hasGeoRoutingFeature}
          title={!hasGeoRoutingFeature ? 'Geographic routing is a premium feature. Upgrade to access this.' : ''}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm border ${
            hasGeoRoutingFeature
              ? 'bg-[--clinqr-bg-subtle] text-[--clinqr-text-primary] border-[--clinqr-border]/10 hover:bg-gray-200'
              : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed opacity-70'
          }`}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Geographic Condition
          {!hasGeoRoutingFeature && (
            <svg className="w-4 h-4 ml-auto flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 1C6.48 1 2 5.48 2 11s4.48 10 10 10 10-4.48 10-10S17.52 1 12 1zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 7 15.5 7 14 7.67 14 8.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 7 8.5 7 7 7.67 7 8.5 7.67 10 8.5 10zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z" />
            </svg>
          )}
        </button>
        <button
          type="button"
          onClick={() => handleAddCondition('device')}
          className="flex items-center gap-2 px-3 py-2 bg-[--clinqr-bg-subtle] text-[--clinqr-text-primary] border border-[--clinqr-border]/10 rounded-lg hover:bg-gray-200 transition-colors text-sm"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Device Condition
        </button>
      </div>
    </div>
  );
}
